/**
 * Native implementations to replace lodash functions
 */

/**
 * Checks if a value is empty (array, object, string, null, undefined)
 */
export const isEmpty = (value: unknown): boolean => {
  if (value == null) {
    return true;
  }
  if (Array.isArray(value) || typeof value === "string") {
    return value.length === 0;
  }
  if (typeof value === "object") {
    return Object.keys(value).length === 0;
  }
  return false;
};

/**
 * Deep merge objects (similar to lodash.merge)
 */
export const merge = <T extends object>(target: T, ...sources: (Partial<T> | null | undefined)[]): T => {
  const result = { ...target } as Record<string, unknown>;

  sources.forEach((source) => {
    if (!source) {
      return;
    }

    Object.keys(source).forEach((key) => {
      const sourceValue = (source as Record<string, unknown>)[key];
      const targetValue = result[key];

      if (
        sourceValue &&
        typeof sourceValue === "object" &&
        !Array.isArray(sourceValue) &&
        targetValue &&
        typeof targetValue === "object" &&
        !Array.isArray(targetValue)
      ) {
        result[key] = merge(targetValue as Record<string, unknown>, sourceValue as Record<string, unknown>);
      } else {
        result[key] = sourceValue;
      }
    });
  });

  return result as unknown as T;
};

/**
 * Creates an array with n elements, calling iteratee for each index
 */
export const times = <T = number>(n: number, iteratee?: (index: number) => T): (T | number)[] => {
  const indices = Array.from({ length: n }, (_, i) => i);
  return iteratee ? indices.map(iteratee) : indices;
};
